<template>
	<nav class="header-nav">
		<template v-for="link in visibleLinks" :key="link.label">
			<router-link
				v-if="!link.isStub && link.route"
				v-slot="{ href, navigate }"
				:to="linkTarget(link)"
				custom
			>
				<a
					:href="href"
					class="header-nav__link"
					:class="{ 'header-nav__link--active': isActive(link) }"
					@click="onClick(link, navigate, $event)"
				>
					{{ link.label }}
				</a>
			</router-link>
			<span
				v-else
				class="header-nav__link header-nav__link--stub"
			>
				{{ link.label }}
			</span>
		</template>
	</nav>
</template>

<script setup lang="ts">
import { i18n } from "@/i18n";
import { computed } from "vue"
import { useRoute } from "vue-router"
import type { RouteLocationRaw } from "vue-router"
import { workerStore } from "@/store/worker"
import { chatsStore } from "@/store/chats"
import { rootStore } from "@/store/index"
import type { Worker } from "@/ api/WorkerApi"

type AccessKey = "CalendarAccess" | "AnalyticsAccess" | "StorageAccess"

type NavLink = {
	label: string
	route?: string
	accessKey?: AccessKey
	isStub: boolean
}

const route = useRoute()
const wStore = workerStore()
const root = rootStore()

const links = computed<NavLink[]>(() => [
	{ label: String(i18n.global.t('page_title_main')), route: "/main", isStub: false },
	{ label: String(i18n.global.t('page_title_calendar')), route: "/calendar", accessKey: "CalendarAccess", isStub: false },
	{ label: String(i18n.global.t('page_title_analytics')), route: "/analytics", accessKey: "AnalyticsAccess", isStub: false },
	{ label: String(i18n.global.t('page_title_settings')), route: "/settings", isStub: false },
	{ label: String(i18n.global.t('page_title_storage')), route: "/storage", accessKey: "StorageAccess", isStub: false }
])

const isAllowed = (link: NavLink, profile: Worker | null) => {
	if (!link.accessKey) {
		return true
	}
	if (!profile) {
		return false
	}
	const value = profile[link.accessKey]
	if (link.accessKey === "StorageAccess") {
		return Number(value) > 0
	}
	return Boolean(value)
}

const visibleLinks = computed(() =>
	links.value.filter(link => isAllowed(link, wStore.workerProfile))
)

const isHomeLink = (link: NavLink) => link.route === '/main'
// «Аналитика» и «Склад» открывают одну страницу Аналитики с разными сегментами.
// /storage роутер перенаправляет на сегмент «Позиции склада».
const isAnalyticsLink = (link: NavLink) => link.route === '/analytics'
const isStorageLink = (link: NavLink) => link.route === '/storage'

const linkTarget = (link: NavLink): RouteLocationRaw => {
	if (isHomeLink(link)) {
		return {
			path: '/main',
			query: {
				page: 'chat',
				mode: 'list'
			}
		}
	}
	if (isAnalyticsLink(link)) {
		return {
			path: '/analytics',
			query: { segment: String(root.lastAnalyticsMode) }
		}
	}
	return link.route as string
}

const isActive = (link: NavLink) => {
	if (isHomeLink(link)) {
		return route.path === '/main' && route.query.page === 'chat'
	}
	if (isAnalyticsLink(link)) {
		return route.path === '/analytics' && !root.isStorageMode
	}
	if (isStorageLink(link)) {
		return route.path === '/analytics' && root.isStorageMode
	}
	return link.route === route.path
}

const onClick = (
	link: NavLink,
	navigate: (e: MouseEvent) => void,
	e: MouseEvent
) => {
	// Модифицирующий/средний клик — отдаём браузеру, чтобы открылась новая вкладка
	if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
		return
	}

	if (isHomeLink(link)) {
		chatsStore().setMode('list')
		chatsStore().setOpenedChat(null)
	}

	navigate(e)
}
</script>

<style scoped>
.header-nav {
	display: flex;
	align-items: center;
	gap: 24px;
}

.header-nav__link {
	display: inline-block;
	background: transparent;
	border: none;
	color: #fff;
	font-size: 20px;
	font-weight: 400;
	line-height: 1;
	padding: 4px 0;
	border-bottom: 2px solid transparent;
	cursor: pointer;
	text-decoration: none;
	font-family: inherit;
}

.header-nav__link:hover,
.header-nav__link:focus,
.header-nav__link:visited {
	color: #fff;
}

.header-nav__link--stub {
	cursor: default;
}

.header-nav__link--active {
	border-bottom-color: #fff;
}
</style>
